/**
 * 2.5-D column look for islands and rocks: the top face leans screen-up (−Y), same
 * convention as checkpoint poles. Collision and hit-tests stay on the waterline.
 */

import { PIXELS_PER_METER } from '../constants.ts';
import { polygonBounds } from '../geometry.ts';
import { clamp } from '../math.ts';
import type { Vec2 } from '../types.ts';
import { WATER_CLIP_STEP, waterSpans } from './checkpointLook.ts';
import { pointInPolygon } from './islands.ts';
import type { Sun } from './sun.ts';

/** How much of the real elevation becomes a visible screen-up lean. */
export const EXTRUDE_SCALE = 0.35;
/** Floor so a 1 m rock still shows a sliver of cliff. No ceiling — 50 m must look taller than 10 m. */
export const EXTRUDE_MIN_PX = 6;
export const WALL_AMBIENT = 0.22;
export const TOP_AMBIENT = 0.55;
/** Previous hard-coded dash opacity. Play Debug can override; migrate fills this. */
export const DEFAULT_WATERLINE_HINT_ALPHA = 0.7;
/** A shorter lip does not hide the shore, so it gets no waterline dash. */
export const WATERLINE_HINT_MIN_ELEVATION_M = 5;
/** First outward probe so the sample sits in water, not on the waterline. */
const WATERLINE_HINT_OUTWARD_PX = 1;
/** World-pixel shore / shelf contact band. Independent of azimuth. */
export const CONTACT_WIDTH_PX = 8;
/** Thin crease stroke on wall water-edge and cap lip. */
export const CREASE_STROKE_PX = 2.5;

export type CreaseKind = 'water' | 'lip' | 'shelf';

/** 0–1 mix toward 0x1a1210. Shelf contact is strongest; the cap lip is a hint. */
export function creaseShade(kind: CreaseKind): number {
  if (kind === 'shelf') {
    return 0.42;
  }

  if (kind === 'lip') {
    return 0.2;
  }

  return 0.34;
}

export type WallQuad = {
  waterA: Vec2;
  waterB: Vec2;
  topA: Vec2;
  topB: Vec2;
  outward: Vec2;
  midY: number;
};

/** Screen pixels the plateau top rises. Zero elevation stays flat. */
export function visualRisePx(elevationM: number): number {
  if (elevationM <= 0) {
    return 0;
  }

  return Math.max(EXTRUDE_MIN_PX, elevationM * PIXELS_PER_METER * EXTRUDE_SCALE);
}

/** Screen-up offset of the top face, matching `poleCap`. */
export function extrudeOffset(elevationM: number): Vec2 {
  const rise = visualRisePx(elevationM);

  return { x: 0, y: rise === 0 ? 0 : -rise };
}

export type ColumnAabb = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

/**
 * Cached under-cap probe: waterline footprint, screen-up top, and the extruded
 * AABB. Built once per outline + elevation, not per sample.
 */
export type PlateauProbe = {
  footprint: readonly Vec2[];
  top: readonly Vec2[];
  box: ColumnAabb | null;
  elevation: number;
};

const plateauByOutline = new WeakMap<object, PlateauProbe>();

/** Offset top + extruded AABB for `pointUnderPlateau` without a per-call `map()`. */
export function buildPlateauProbe(
  waterline: readonly Vec2[],
  elevation: number,
): PlateauProbe {
  const offset = extrudeOffset(elevation);
  const box = visualColumnBounds(waterline, elevation);
  const top =
    offset.x === 0 && offset.y === 0
      ? waterline
      : waterline.map((vertex) => ({
          x: vertex.x + offset.x,
          y: vertex.y + offset.y,
        }));

  return { footprint: waterline, top, box, elevation };
}

/**
 * Reuses a WeakMap keyed by the tessellated outline. Elevation-stack callers
 * keep their own probe on the stack entry instead.
 */
export function plateauProbe(
  waterline: readonly Vec2[],
  elevation: number,
): PlateauProbe {
  const hit = plateauByOutline.get(waterline);

  if (hit && hit.elevation === elevation && hit.footprint === waterline) {
    return hit;
  }

  const next = buildPlateauProbe(waterline, elevation);
  plateauByOutline.set(waterline, next);
  return next;
}

export function pointInColumnAabb(point: Vec2, box: ColumnAabb): boolean {
  return (
    point.x >= box.minX &&
    point.x <= box.maxX &&
    point.y >= box.minY &&
    point.y <= box.maxY
  );
}

/** Visual click on the plateau → waterline coords stored on the document. */
export function waterlineFromPlateau(point: Vec2, elevationM: number): Vec2 {
  const offset = extrudeOffset(elevationM);

  return { x: point.x - offset.x, y: point.y - offset.y };
}

/** Waterline coords → where the plateau (and props) are drawn. */
export function plateauFromWaterline(point: Vec2, elevationM: number): Vec2 {
  const offset = extrudeOffset(elevationM);

  return { x: point.x + offset.x, y: point.y + offset.y };
}

/**
 * Axis-aligned box of the painted column: waterline footprint plus the
 * screen-up plateau. A disk that misses the waterline can still hit this.
 */
export function visualColumnBounds(
  outline: readonly Vec2[],
  elevation: number,
  baseElevation = 0,
): { minX: number; minY: number; maxX: number; maxY: number } | null {
  const water = polygonBounds(outline);

  if (!water) {
    return null;
  }

  const offset = extrudeOffset(elevation);
  const base = extrudeOffset(Math.min(baseElevation, elevation));

  return {
    minX: Math.min(water.minX + base.x, water.minX + offset.x),
    minY: Math.min(water.minY + base.y, water.minY + offset.y),
    maxX: Math.max(water.maxX + base.x, water.maxX + offset.x),
    maxY: Math.max(water.maxY + base.y, water.maxY + offset.y),
  };
}

function pointInAabb(point: Vec2, box: ColumnAabb): boolean {
  return pointInColumnAabb(point, box);
}

/**
 * Screen point sits on the painted column: the waterline swept screen-up by
 * `visualRisePx`. Union of footprint and cap misses the cliff band when the
 * body is shorter in Y than the rise — the walls still fill that gap.
 */
export function pointInColumn(
  waterline: readonly Vec2[],
  elevation: number,
  point: Vec2,
  baseElevation = 0,
): boolean {
  if (waterline.length < 3) {
    return false;
  }

  const rise = visualRisePx(elevation);
  const baseRise = visualRisePx(Math.min(baseElevation, elevation));

  if (rise <= 0) {
    return pointInPolygon(point, waterline);
  }

  const box = visualColumnBounds(waterline, elevation, baseElevation);

  if (!box || !pointInAabb(point, box)) {
    return false;
  }

  const southTop = { x: point.x, y: point.y + rise };

  if (baseRise <= 0) {
    return (
      pointInPolygon(point, waterline) ||
      pointInPolygon(southTop, waterline) ||
      verticalSegmentHitsPolygon(point.x, point.y, southTop.y, waterline)
    );
  }

  return (
    pointInPolygon(southTop, waterline) ||
    verticalSegmentHitsPolygon(point.x, point.y + baseRise, southTop.y, waterline)
  );
}

/** Vertical segment hits the filled footprint (inclusive of edges). */
function verticalSegmentHitsPolygon(
  x: number,
  y0: number,
  y1: number,
  polygon: readonly Vec2[],
): boolean {
  const lo = Math.min(y0, y1);
  const hi = Math.max(y0, y1);
  const count = polygon.length;

  for (let i = 0; i < count; i += 1) {
    const a = polygon[i];
    const b = polygon[(i + 1) % count];

    if (!a || !b) {
      continue;
    }

    const dx = b.x - a.x;

    if (Math.abs(dx) < 1e-9) {
      if (Math.abs(a.x - x) > 1e-6) {
        continue;
      }

      const edgeLo = Math.min(a.y, b.y);
      const edgeHi = Math.max(a.y, b.y);

      if (edgeHi >= lo && edgeLo <= hi) {
        return true;
      }

      continue;
    }

    const t = (x - a.x) / dx;

    if (t < 0 || t > 1) {
      continue;
    }

    const y = a.y + t * (b.y - a.y);

    if (y >= lo && y <= hi) {
      return true;
    }
  }

  return false;
}

/**
 * Legal water hidden by the screen-up cap: inside the offset top, outside the
 * waterline footprint. Collision stays on the waterline — this is look only.
 */
export function pointUnderPlateauProbe(probe: PlateauProbe, point: Vec2): boolean {
  if (probe.top === probe.footprint || !probe.box) {
    return false;
  }

  if (!pointInAabb(point, probe.box) || pointInPolygon(point, probe.footprint)) {
    return false;
  }

  return pointInHiddenWater(point, probe.footprint, probe.top, probe.box);
}

export function pointUnderPlateau(
  waterline: readonly Vec2[],
  elevation: number,
  point: Vec2,
): boolean {
  return pointUnderPlateauProbe(plateauProbe(waterline, elevation), point);
}

function pointInHiddenWater(
  point: Vec2,
  footprint: readonly Vec2[],
  top: readonly Vec2[],
  box: { minX: number; minY: number; maxX: number; maxY: number },
): boolean {
  return (
    pointInAabb(point, box) &&
    !pointInPolygon(point, footprint) &&
    pointInPolygon(point, top)
  );
}

/** True when an outward probe within the rise sits under the cap. */
function shoreHasHiddenWater(
  point: Vec2,
  outward: Vec2,
  rise: number,
  footprint: readonly Vec2[],
  top: readonly Vec2[],
  box: { minX: number; minY: number; maxX: number; maxY: number },
): boolean {
  const probe = (distance: number): boolean =>
    pointInHiddenWater(
      { x: point.x + outward.x * distance, y: point.y + outward.y * distance },
      footprint,
      top,
      box,
    );

  if (probe(WATERLINE_HINT_OUTWARD_PX)) {
    return true;
  }

  for (let distance = WATER_CLIP_STEP; distance < rise; distance += WATER_CLIP_STEP) {
    if (probe(distance)) {
      return true;
    }
  }

  return rise > WATERLINE_HINT_OUTWARD_PX && probe(rise);
}

function signedArea(points: readonly Vec2[]): number {
  let area = 0;

  for (let i = 0; i < points.length; i += 1) {
    const a = points[i];
    const b = points[(i + 1) % points.length];

    if (!a || !b) {
      continue;
    }

    area += a.x * b.y - b.x * a.y;
  }

  return area;
}

function unit(x: number, y: number): Vec2 {
  const length = Math.hypot(x, y);

  if (length < 1e-8) {
    return { x: 0, y: 0 };
  }

  return { x: x / length || 0, y: y / length || 0 };
}

/**
 * Cliff quads from each edge to the offset top, north-first so a painter's
 * algorithm can draw far walls before near ones. `baseOffset` is the support
 * plateau — a stacked terrace only extrudes the riser, not a pillar from the
 * waterline through the land it sits on.
 */
export function wallQuads(
  footprint: readonly Vec2[],
  offset: Vec2,
  baseOffset: Vec2 = { x: 0, y: 0 },
): WallQuad[] {
  if (
    footprint.length < 3 ||
    (offset.x === baseOffset.x && offset.y === baseOffset.y)
  ) {
    return [];
  }

  const ccw = signedArea(footprint) > 0;
  const quads: WallQuad[] = [];

  for (let i = 0; i < footprint.length; i += 1) {
    const waterA = footprint[i];
    const waterB = footprint[(i + 1) % footprint.length];

    if (!waterA || !waterB) {
      continue;
    }

    const outward = edgeOutward(waterA, waterB, ccw);

    if (outward.x === 0 && outward.y === 0) {
      continue;
    }

    const baseA = { x: waterA.x + baseOffset.x, y: waterA.y + baseOffset.y };
    const baseB = { x: waterB.x + baseOffset.x, y: waterB.y + baseOffset.y };
    const topA = { x: waterA.x + offset.x, y: waterA.y + offset.y };
    const topB = { x: waterB.x + offset.x, y: waterB.y + offset.y };

    quads.push({
      waterA: baseA,
      waterB: baseB,
      topA,
      topB,
      outward,
      midY: (baseA.y + baseB.y) * 0.5,
    });
  }

  quads.sort((a, b) => a.midY - b.midY);
  return quads;
}

function edgeOutward(a: Vec2, b: Vec2, ccw: boolean): Vec2 {
  const dx = b.x - a.x;
  const dy = b.y - a.y;

  if (Math.hypot(dx, dy) < 1e-6) {
    return { x: 0, y: 0 };
  }

  return ccw ? unit(dy, -dx) : unit(-dy, dx);
}

function sameHintPoint(a: Vec2, b: Vec2): boolean {
  return Math.abs(a.x - b.x) < 1e-6 && Math.abs(a.y - b.y) < 1e-6;
}

/** Slide off a vertex toward the edge mid so a corner probe is not on the cap rim. */
function originTowardEdgeInterior(point: Vec2, a: Vec2, b: Vec2): Vec2 {
  const dx = (a.x + b.x) * 0.5 - point.x;
  const dy = (a.y + b.y) * 0.5 - point.y;
  const length = Math.hypot(dx, dy);

  if (length < 1e-8) {
    return point;
  }

  const inset = Math.min(WATERLINE_HINT_OUTWARD_PX * 0.5, length);
  return { x: point.x + (dx / length) * inset, y: point.y + (dy / length) * inset };
}

/**
 * Waterline polylines whose outward water sits under the cap. Walks each edge
 * like a gate clip so a concave bay keeps only the stretch the lean actually
 * covers. Ring order is kept so a dash can walk a hidden run without restarting.
 * Not the finished hidden-shore look — see docs/LIGHTING.md.
 */
export function hiddenWaterlineRuns(
  footprint: readonly Vec2[],
  elevation: number,
): Vec2[][] {
  const rise = visualRisePx(elevation);

  if (footprint.length < 3 || rise <= 0) {
    return [];
  }

  const box = visualColumnBounds(footprint, elevation);

  if (!box) {
    return [];
  }

  const ccw = signedArea(footprint) > 0;
  const offset = extrudeOffset(elevation);
  const top = footprint.map((vertex) => ({
    x: vertex.x + offset.x,
    y: vertex.y + offset.y,
  }));
  const n = footprint.length;
  const kept: Vec2[][] = [];
  let current: Vec2[] | null = null;

  const flush = (): void => {
    if (current) {
      kept.push(current);
      current = null;
    }
  };

  const appendSpan = (a: Vec2, b: Vec2): void => {
    const last = current?.[current.length - 1];

    if (current && last && sameHintPoint(last, a)) {
      current.push(b);
      return;
    }

    flush();
    current = [a, b];
  };

  for (let i = 0; i < n; i += 1) {
    const a = footprint[i];
    const b = footprint[(i + 1) % n];

    if (!a || !b) {
      flush();
      continue;
    }

    const outward = edgeOutward(a, b, ccw);

    if (outward.x === 0 && outward.y === 0) {
      flush();
      continue;
    }

    const exposed = (point: Vec2): boolean =>
      !shoreHasHiddenWater(
        originTowardEdgeInterior(point, a, b),
        outward,
        rise,
        footprint,
        top,
        box,
      );
    const spans = waterSpans(a, b, exposed);

    if (spans.length === 0) {
      flush();
      continue;
    }

    for (const span of spans) {
      appendSpan(span.a, span.b);
    }
  }

  flush();

  if (kept.length >= 2) {
    const first = kept[0];
    const last = kept[kept.length - 1];
    const firstStart = first?.[0];
    const lastEnd = last?.[last.length - 1];

    if (first && last && firstStart && lastEnd && sameHintPoint(firstStart, lastEnd)) {
      kept[0] = [...last, ...first.slice(1)];
      kept.pop();
    }
  }

  return kept;
}

export type WaterlineHintSolid = {
  footprint: readonly Vec2[];
  elevation: number;
};

/**
 * Another body hides this shore when the point is on its waterline (stacked
 * terrace) or its raised top (the plateau). Cliff walls do not count — the dash
 * is a waterline mark and must stay visible across an elevation face.
 */
function pointOnOtherPlateauOrFootprint(
  point: Vec2,
  solids: readonly WaterlineHintSolid[],
): boolean {
  for (const solid of solids) {
    if (solid.footprint.length < 3) {
      continue;
    }

    if (pointInPolygon(point, solid.footprint)) {
      return true;
    }

    const offset = extrudeOffset(solid.elevation);

    if (offset.x === 0 && offset.y === 0) {
      continue;
    }

    const top = solid.footprint.map((vertex) => ({
      x: vertex.x + offset.x,
      y: vertex.y + offset.y,
    }));

    if (pointInPolygon(point, top)) {
      return true;
    }
  }

  return false;
}

/**
 * Drop stretches that sit on another footprint or plateau. Walk each edge like
 * a gate clip so a long shore gaps around a stacked cap instead of keeping or
 * dropping the whole chord. Walls are not occluders.
 */
function clipWaterlineRunAgainstSolids(
  run: readonly Vec2[],
  solids: readonly WaterlineHintSolid[],
): Vec2[][] {
  if (run.length < 2) {
    return [];
  }

  if (solids.length === 0) {
    return [run.slice()];
  }

  const covered = (point: Vec2): boolean => pointOnOtherPlateauOrFootprint(point, solids);
  const kept: Vec2[][] = [];
  let current: Vec2[] | null = null;

  const appendSpan = (a: Vec2, b: Vec2): void => {
    const last = current?.[current.length - 1];

    if (current && last && last.x === a.x && last.y === a.y) {
      current.push(b);
      return;
    }

    if (current) {
      kept.push(current);
    }

    current = [a, b];
  };

  for (let i = 0; i < run.length - 1; i += 1) {
    const a = run[i];
    const b = run[i + 1];

    if (!a || !b) {
      continue;
    }

    const spans = waterSpans(a, b, covered);

    if (spans.length === 0) {
      if (current) {
        kept.push(current);
        current = null;
      }

      continue;
    }

    for (const span of spans) {
      appendSpan(span.a, span.b);
    }
  }

  if (current) {
    kept.push(current);
  }

  return kept;
}

/**
 * Hidden-shore dashes the boat can actually meet: skip columns under 5 m, and
 * skip shoreline that sits on another footprint or plateau. A stack keeps the
 * lowest exposed run; a cliff in front of that run does not hide it. An
 * overhang into open water still hints that exposed stretch only.
 */
export function exposedNorthWaterlineRuns(
  footprint: readonly Vec2[],
  elevation: number,
  otherSolids: readonly WaterlineHintSolid[] = [],
): Vec2[][] {
  if (elevation < WATERLINE_HINT_MIN_ELEVATION_M) {
    return [];
  }

  const runs: Vec2[][] = [];

  for (const run of hiddenWaterlineRuns(footprint, elevation)) {
    for (const clipped of clipWaterlineRunAgainstSolids(run, otherSolids)) {
      if (clipped.length >= 2) {
        runs.push(clipped);
      }
    }
  }

  return runs;
}

/** 0–1 illumination of a vertical cliff from the sun's ground direction. */
export function wallLambert(outward: Vec2, sun: Sun): number {
  const ground = Math.hypot(sun.direction.x, sun.direction.y);
  const facing =
    ground < 1e-6 ? 0 : (outward.x * sun.direction.x + outward.y * sun.direction.y) / ground;
  const diffuse = Math.max(0, facing) * sun.irradiance;

  return clamp(WALL_AMBIENT * sun.skyLight + diffuse, 0, 1);
}

/** Horizontal plateau: N is +Z, so noon is bright and a low sun stays flatter. */
export function topLambert(sun: Sun): number {
  const diffuse = Math.max(0, sun.direction.z) * sun.irradiance;

  return clamp(TOP_AMBIENT * sun.skyLight + diffuse, 0, 1);
}
